import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, MapPinned, RotateCcw, Users, KanbanSquare, Globe2, Handshake, FileText,
  Building2, TrendingUp, ArrowLeft, ChevronRight, CalendarRange, AlertTriangle, Download, Printer,
} from "lucide-react";
import { downloadTerritoryCsv, openTerritoryPrintReport } from "./territoryExport";

const NAVY = "#1C4358";
const RED = "#9C1E22";
const GREEN = "#2F7D5A";
const AMBER = "#B7791F";
const SKY = "#2C6480";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const EMPTY = {
  date_from: daysAgo(30), date_to: today(), country_id: "", state_province_id: "",
  metro_region_id: "", city_id: "", service_area_id: "", franchise_id: "",
  branch_id: "", service_lane: "", source: "",
};

const Tile = ({ label, value, tone = NAVY, testid }) => (
  <div className="rounded-xl border border-[#EEF3F6] bg-white p-3" data-testid={testid}>
    <div className="text-2xl font-bold tabular-nums" style={{ color: tone }}>{value ?? 0}</div>
    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{label}</div>
  </div>
);

const Bars = ({ items, tone = NAVY, testid, empty = "No data in this range." }) => {
  const rows = (items || []).filter((r) => r.name);
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="text-[12px] text-muted-foreground py-2" data-testid={testid}>{empty}</p>;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-[#4B5563] truncate pr-2">{r.name}</span>
            <span className="font-bold text-[#1C4358] tabular-nums">{r.value}</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#EEF3F6] mt-0.5">
            <div className="h-full rounded-full" style={{ width: `${(r.value / max) * 100}%`, backgroundColor: tone }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Section = ({ icon: Icon, title, tone = NAVY, children, testid }) => (
  <Card className="p-4 sm:p-5 border-[#E6EDF2]" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tone}14`, color: tone }}>
        <Icon size={16} />
      </span>
      <h3 className="font-display font-bold text-[#252221] text-[15px]">{title}</h3>
    </div>
    {children}
  </Card>
);

const FSelect = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="h-9 w-full sm:w-[160px] rounded-lg text-xs font-medium bg-white" data-testid={testid}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}</SelectItem>
      {(options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function TerritoryReports() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ ...EMPTY, franchise_id: id || "" });
  const [summary, setSummary] = useState(null);
  const [scorecards, setScorecards] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/reports/territories/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { if (id) setFilters((f) => ({ ...f, franchise_id: id })); }, [id]);

  const params = useMemo(() => Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sc] = await Promise.all([
        api.get("/reports/territories/summary", { params }),
        api.get("/reports/territories/scorecards", { params: { date_from: filters.date_from, date_to: filters.date_to } }),
      ]);
      setSummary(s.data);
      setScorecards(sc.data);
    } finally { setLoading(false); }
  }, [params, filters.date_from, filters.date_to]);

  useEffect(() => { load(); }, [load]);

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const la = summary?.lead_activity || {};

  // Human-readable summary of active filters — powers the CSV header + printable report.
  const filterLines = useMemo(() => {
    const nameOf = (list, id) => (list || []).find((x) => x.id === id)?.name || id;
    const out = [`Date range: ${filters.date_from} to ${filters.date_to}`];
    if (filters.country_id) out.push(`Country: ${nameOf(meta?.countries, filters.country_id)}`);
    if (filters.state_province_id) out.push(`State/Province: ${nameOf(meta?.states, filters.state_province_id)}`);
    if (filters.metro_region_id) out.push(`Territory: ${nameOf(meta?.metros, filters.metro_region_id)}`);
    if (filters.city_id) out.push(`City: ${nameOf(meta?.cities, filters.city_id)}`);
    if (filters.service_area_id) out.push(`Service area: ${nameOf(meta?.service_areas, filters.service_area_id)}`);
    if (filters.franchise_id) out.push(`Franchise: ${nameOf(meta?.franchises, filters.franchise_id)}`);
    if (filters.branch_id) out.push(`Branch: ${nameOf(meta?.branches, filters.branch_id)}`);
    if (filters.service_lane) out.push(`Service lane: ${(meta?.service_lanes || []).find((l) => l.value === filters.service_lane)?.label || filters.service_lane}`);
    if (filters.source) out.push(`Source: ${(meta?.sources || []).find((s) => s.value === filters.source)?.label || filters.source}`);
    return out;
  }, [filters, meta]);

  const scopeLabel = meta?.scope_label || "";
  const exportCsv = () => downloadTerritoryCsv(summary, scorecards, filterLines);
  const printReport = () => openTerritoryPrintReport(summary, scorecards, filterLines, scopeLabel);

  const pipe = summary?.pipeline_activity || {};
  const lp = summary?.local_pages || {};
  const ext = summary?.external_referrals || {};
  const dm = summary?.documents_meetings || {};
  const sl = summary?.service_lanes || {};
  const activeCount = Object.entries(filters).filter(([k, v]) => v && !["date_from", "date_to"].includes(k)).length;

  return (
    <div className="pb-12 space-y-5" data-testid="territory-reports-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <MapPinned size={22} className="text-[#1C4358]" />
            <h1 className="text-2xl font-display font-bold text-[#1C4358]">Territory Reports</h1>
          </div>
          <p className="text-sm text-muted-foreground">{meta?.scope_label || "Performance by country, state, territory, city, franchise & branch."}</p>
        </div>
        {id && (
          <Button variant="outline" size="sm" onClick={() => nav("/reports/territories")} className="border-[#D6E0E7] text-[#1C4358] gap-1" data-testid="back-to-all-territories">
            <ArrowLeft size={14} /> All territories
          </Button>
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!summary} className="border-[#D6E0E7] text-[#1C4358] gap-1.5" data-testid="export-csv-btn">
            <Download size={14} /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={printReport} disabled={!summary} className="border-[#D6E0E7] text-[#1C4358] gap-1.5" data-testid="print-report-btn">
            <Printer size={14} /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5">
        <button onClick={() => setTab("dashboard")} data-testid="tab-dashboard"
          className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border ${tab === "dashboard" ? "bg-[#1C4358] text-white border-[#1C4358]" : "bg-white text-[#4B5563] border-[#E6EDF2]"}`}>Dashboard</button>
        <button onClick={() => setTab("scorecards")} data-testid="tab-scorecards"
          className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border ${tab === "scorecards" ? "bg-[#1C4358] text-white border-[#1C4358]" : "bg-white text-[#4B5563] border-[#E6EDF2]"}`}>Territory scorecards</button>
      </div>

      {/* Filters */}
      <Card className="p-3 sm:p-4 border-[#E6EDF2]" data-testid="territory-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1C4358] mr-1">
            <CalendarRange size={14} /> Range
          </span>
          <input type="date" value={filters.date_from} onChange={(e) => setF("date_from")(e.target.value)}
            className="h-9 rounded-lg border border-[#D6E0E7] px-2 text-xs bg-white" data-testid="filter-date-from" />
          <span className="text-xs text-muted-foreground">to</span>
          <input type="date" value={filters.date_to} onChange={(e) => setF("date_to")(e.target.value)}
            className="h-9 rounded-lg border border-[#D6E0E7] px-2 text-xs bg-white" data-testid="filter-date-to" />
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-2">
          <FSelect testid="filter-country" value={filters.country_id} onChange={setF("country_id")} placeholder="All countries"
            options={(meta?.countries || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-state" value={filters.state_province_id} onChange={setF("state_province_id")} placeholder="All states/provinces"
            options={(meta?.states || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-metro" value={filters.metro_region_id} onChange={setF("metro_region_id")} placeholder="All territories"
            options={(meta?.metros || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-city" value={filters.city_id} onChange={setF("city_id")} placeholder="All cities"
            options={(meta?.cities || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-service-area" value={filters.service_area_id} onChange={setF("service_area_id")} placeholder="All service areas"
            options={(meta?.service_areas || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-franchise" value={filters.franchise_id} onChange={setF("franchise_id")} placeholder="All franchises"
            options={(meta?.franchises || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-branch" value={filters.branch_id} onChange={setF("branch_id")} placeholder="All branches"
            options={(meta?.branches || []).map((c) => ({ value: c.id, label: c.name }))} />
          <FSelect testid="filter-lane" value={filters.service_lane} onChange={setF("service_lane")} placeholder="All service lanes"
            options={(meta?.service_lanes || []).map((c) => ({ value: c.value, label: c.label }))} />
          <FSelect testid="filter-source" value={filters.source} onChange={setF("source")} placeholder="All sources"
            options={(meta?.sources || []).map((c) => ({ value: c.value, label: c.label }))} />
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setFilters((f) => ({ ...EMPTY, date_from: f.date_from, date_to: f.date_to }))}
              className="text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" data-testid="filter-reset">
              <RotateCcw size={14} className="mr-1" /> Reset
            </Button>
          )}
          <span className="text-[12px] text-muted-foreground ml-auto">
            {summary?.branch_count === null ? "All branches" : `${summary?.branch_count ?? 0} branch(es)`}
          </span>
        </div>
      </Card>

      {loading && !summary ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1C4358]" /></div>
      ) : tab === "dashboard" ? (
        <div className="space-y-4">
          {/* 1. Lead Activity */}
          <Section icon={Users} title="Lead Activity" tone={NAVY} testid="section-lead-activity">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4">
              <Tile label="Total leads (all-time)" value={la.total_leads} testid="stat-total-leads" />
              <Tile label="New leads (range)" value={la.new_leads} tone={GREEN} testid="stat-new-leads" />
              <Tile label="Consultation requests" value={la.consultation_requests} tone={SKY} testid="stat-consults" />
              <Tile label="Completed intake forms" value={la.completed_intake_forms} testid="stat-intake" />
              <Tile label="Live chat leads" value={la.live_chat_leads} tone={SKY} testid="stat-chat" />
              <Tile label="Local page leads" value={la.local_page_leads} testid="stat-local" />
              <Tile label="Reverse search leads" value={la.reverse_search_leads} testid="stat-reverse" />
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">By service lane</div><Bars items={la.by_service_lane} testid="bars-lead-lane" /></div>
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">By urgency</div><Bars items={la.by_urgency} tone={RED} testid="bars-lead-urgency" /></div>
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">By channel</div><Bars items={la.by_channel} tone={SKY} testid="bars-lead-channel" /></div>
            </div>
          </Section>

          {/* 2. Pipeline Activity */}
          <Section icon={KanbanSquare} title="Pipeline Activity" tone={GREEN} testid="section-pipeline-activity">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4">
              <Tile label="Open opportunities" value={pipe.open_opportunities} testid="stat-open-opps" />
              <Tile label="Consultations booked" value={pipe.consultations_booked} tone={SKY} testid="stat-consults-booked" />
              <Tile label="Roadmaps created" value={pipe.roadmaps_created} testid="stat-roadmaps" />
              <Tile label="Referrals sent" value={pipe.referrals_sent} testid="stat-refs-sent" />
              <Tile label="Closed won" value={pipe.closed_won} tone={GREEN} testid="stat-closed-won" />
              <Tile label="Closed lost" value={pipe.closed_lost} tone={RED} testid="stat-closed-lost" />
              <Tile label="Long-term follow-up" value={pipe.long_term_follow_up} tone={AMBER} testid="stat-ltf" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Lead pipeline stages</div><Bars items={pipe.pipeline_stage_breakdown} testid="bars-pipeline-stages" /></div>
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Opportunity stages</div><Bars items={pipe.opportunity_stage_breakdown} tone={GREEN} testid="bars-opp-stages" /></div>
            </div>
          </Section>

          {/* 3. Local Page Performance */}
          <Section icon={Globe2} title="Local Page Performance" tone={SKY} testid="section-local-pages">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-4">
              <Tile label="Local page leads" value={lp.local_page_leads} testid="stat-lp-leads" />
              <Tile label="Local service-lane page leads" value={lp.local_service_lane_page_leads} testid="stat-lp-lane-leads" />
              <Tile label="Coverage lookups" value={lp.coverage_lookup_submissions ?? "—"} testid="stat-lp-coverage" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">CTA source breakdown</div><Bars items={lp.cta_source_breakdown} tone={SKY} testid="bars-lp-cta" empty="No local-page leads in this range yet." /></div>
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Top local pages by leads</div><Bars items={lp.top_local_pages} testid="bars-lp-top" empty="No local-page leads in this range yet." /></div>
            </div>
          </Section>

          {/* 4. External Referral Activity */}
          <Section icon={Handshake} title="Partner / External Referral Activity" tone={NAVY} testid="section-external">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              <Tile label="Referrals sent" value={ext.sent} testid="stat-ext-sent" />
              <Tile label="Accepted" value={ext.accepted} tone={GREEN} testid="stat-ext-accepted" />
              <Tile label="Declined" value={ext.declined} tone={RED} testid="stat-ext-declined" />
              <Tile label="Awaiting response" value={ext.awaiting_response} tone={AMBER} testid="stat-ext-awaiting" />
              <Tile label="Quote pending" value={ext.quote_pending} testid="stat-ext-quote-pending" />
              <Tile label="Quote uploaded" value={ext.quote_uploaded} testid="stat-ext-quote-uploaded" />
              <Tile label="Completed" value={ext.completed} tone={GREEN} testid="stat-ext-completed" />
              <Tile label="Overdue / stalled" value={ext.overdue_stalled} tone={RED} testid="stat-ext-stalled" />
              <Tile label="All open" value={ext.open} testid="stat-ext-open" />
            </div>
          </Section>

          {/* 5. Document / Meeting Activity */}
          <Section icon={FileText} title="Document / Meeting Activity" tone={AMBER} testid="section-documents">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
              <Tile label="Documents requested" value={dm.documents_requested} testid="stat-doc-requested" />
              <Tile label="Waiting for review" value={dm.documents_waiting_review} tone={AMBER} testid="stat-doc-review" />
              <Tile label="Stale documents" value={dm.stale_documents} tone={RED} testid="stat-doc-stale" />
              <Tile label="Professional reviews pending" value={dm.professional_reviews_pending} testid="stat-doc-prof" />
              <Tile label="Meetings scheduled" value={dm.meetings_scheduled} tone={SKY} testid="stat-meet-scheduled" />
              <Tile label="Meetings completed" value={dm.meetings_completed} tone={GREEN} testid="stat-meet-completed" />
            </div>
          </Section>

          {/* Service lanes rollup */}
          <Section icon={TrendingUp} title="Service Lane Rollup" tone={NAVY} testid="section-service-lanes">
            <div className="grid md:grid-cols-2 gap-5">
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Leads by service lane</div><Bars items={sl.leads_by_lane} testid="bars-sl-leads" /></div>
              <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Opportunities by service lane</div><Bars items={sl.opportunities_by_lane} tone={GREEN} testid="bars-sl-opps" /></div>
            </div>
          </Section>
        </div>
      ) : (
        /* Scorecards */
        <div className="space-y-3" data-testid="scorecards-list">
          {!scorecards ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" /></div> :
            (scorecards.scorecards || []).length === 0 ? <div className="text-center py-16 text-[#4B5563]">No territories in your scope yet.</div> :
              scorecards.scorecards.map((c) => (
                <Card key={c.branch_id || c.franchise_id} className="p-4 border-[#E6EDF2]" data-testid={`scorecard-${c.franchise_id || c.branch_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-9 w-9 rounded-lg bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><Building2 size={16} /></span>
                      <div className="min-w-0">
                        <div className="font-semibold text-[#1C4358] text-[15px] flex items-center gap-2">
                          {c.territory_name}
                          {c.is_legacy && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#9C1E22]/10 text-[#9C1E22]">Super Admin only</span>}
                        </div>
                        <div className="text-[12px] text-[#9AA5B1] truncate">{c.geography_path}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold px-2.5 py-1 rounded-full capitalize" style={{ backgroundColor: `${NAVY}12`, color: NAVY }} data-testid={`scorecard-status-${c.franchise_id || c.branch_id}`}>{c.publish_status}</span>
                      {c.franchise_id && !c.is_legacy && (
                        <Button variant="ghost" size="sm" className="text-[#1C4358] h-8" onClick={() => nav(`/reports/territories/${c.franchise_id}`)} data-testid={`scorecard-open-${c.franchise_id}`}>
                          Details <ChevronRight size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-3">
                    <Sc label="Leads (range)" value={c.leads} />
                    <Sc label="Consultations" value={c.consultations} />
                    <Sc label="Conversion" value={c.enough_data ? `${c.conversion_rate}%` : "Not enough data yet"} muted={!c.enough_data} />
                    <Sc label="Open opps" value={c.open_opportunities} />
                    <Sc label="Referrals sent" value={c.referrals_sent} />
                    <Sc label="Local page leads" value={c.local_page_leads} />
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-3 pt-3 border-t border-[#EEF3F6]">
                    <Sc label="Docs need review" value={c.documents_needing_review} tone={c.documents_needing_review ? AMBER : NAVY} />
                    <Sc label="Stale docs" value={c.stale_documents} tone={c.stale_documents ? RED : NAVY} />
                    <Sc label="Partner awaiting" value={c.external_referrals_awaiting} tone={c.external_referrals_awaiting ? AMBER : NAVY} />
                    <Sc label="Owner" value={c.owner || "—"} />
                    <Sc label="City manager" value={c.city_manager || "—"} />
                    <Sc label="Active partners" value={c.active_partners} />
                  </div>
                  {(c.external_overdue_stalled > 0 || c.stale_documents > 0) && (
                    <div className="mt-3 flex items-center gap-1.5 text-[12px] text-[#9C1E22]">
                      <AlertTriangle size={13} /> Needs attention: {c.stale_documents} stale doc(s), {c.external_overdue_stalled} stalled referral(s).
                    </div>
                  )}
                </Card>
              ))}
        </div>
      )}
    </div>
  );
}

const Sc = ({ label, value, tone = NAVY, muted }) => (
  <div>
    <div className="text-[10px] text-[#9AA5B1] uppercase tracking-wide">{label}</div>
    <div className={`font-bold tabular-nums truncate ${muted ? "text-[11px] text-[#9AA5B1] font-medium normal-case" : "text-[15px]"}`} style={muted ? {} : { color: tone }}>{value ?? "—"}</div>
  </div>
);
